import { HTML_VIEWER_PATH } from "@rakazo/contracts/content-security-policy";
import type { MiniAppValues as Values } from "@rakazo/contracts";
import { MiniAppValues } from "@rakazo/contracts";
import { useEffect, useRef, useState } from "react";
import { miniAppSdk } from "../lib/mini-app-sdk";

export const PREVIEW_CSP =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:; form-action 'none'; base-uri 'none'";

const PREVIEW_GUARD = `<script>
document.addEventListener("click", (event) => {
  const target = event.target;
  const link = target instanceof Element ? target.closest("a[href]") : null;
  if (!link) return;
  const href = link.getAttribute("href") ?? "";
  event.preventDefault();
  if (href.startsWith("#")) location.hash = href;
}, true);
document.addEventListener("submit", (event) => event.preventDefault(), true);
</script>`;

function previewDocument(html: string): string {
  const meta = `<meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}">`;
  const referrer = `<meta name="referrer" content="no-referrer">`;
  return `${meta}${referrer}${PREVIEW_GUARD}${html}`;
}

/**
 * The shell at HTML_VIEWER_PATH is a network document, so it does not inherit the app's CSP the
 * way a srcdoc frame would (which would block the preview's inline scripts). It renders the posted
 * document in a nested sandboxed srcdoc frame under the shell's own CSP.
 */
export function SandboxedHtmlViewer({
  html,
  title,
  storage,
}: {
  html: string;
  title: string;
  storage?: { initial: Values; save: (values: Values) => Promise<void> };
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const save = useRef(storage?.save);
  save.current = storage?.save;
  // Every shell load starts empty, so each one gets the document again.
  const [loads, setLoads] = useState(0);
  useEffect(() => {
    // The sandboxed shell has an opaque origin, so there is no narrower target origin to name.
    if (loads <= 0) return;
    const target = frame.current?.contentWindow;
    if (!target) return;
    if (!storage) {
      target.postMessage(previewDocument(html), "*");
      return;
    }
    const token = crypto.randomUUID();
    const listener = async (event: MessageEvent) => {
      if (
        event.source !== target ||
        event.data?.token !== token ||
        event.data?.type !== "wurkees-app-save"
      )
        return;
      const id = event.data.id;
      if (typeof id !== "string" || id.length > 64) return;
      const parsed = MiniAppValues.safeParse(event.data.values);
      try {
        if (!parsed.success) throw new Error("Invalid app data or storage limit exceeded.");
        await save.current?.(parsed.data);
        target.postMessage({ type: "wurkees-app-result", token, id, ok: true }, "*");
      } catch {
        target.postMessage(
          {
            type: "wurkees-app-result",
            token,
            id,
            ok: false,
            error: "App data could not be saved. Check the save status above the app.",
          },
          "*",
        );
      }
    };
    window.addEventListener("message", listener);
    target.postMessage(
      {
        type: "wurkees-app-init",
        token,
        html: previewDocument(miniAppSdk(token, storage.initial) + html),
      },
      "*",
    );
    return () => window.removeEventListener("message", listener);
    // The initial snapshot is hydrated once. Saving must not reload the running app.
  }, [loads, html, storage?.initial]);
  return (
    <iframe
      ref={frame}
      title={title}
      src={HTML_VIEWER_PATH}
      onLoad={() => setLoads((count) => count + 1)}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      className="h-full w-full border-0 bg-white"
    />
  );
}
